import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { validateLoginForm } from '@/utils/validation';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const setAuthToken = useAuthStore((state) => state.setAuthToken);
  const setUser = useAuthStore((state) => state.setUser);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateLoginForm(email, password);
    if (validationErrors.length > 0) {
      const errorMap = validationErrors.reduce(
        (acc, err) => ({ ...acc, [err.field]: err.message }),
        {}
      );
      setErrors(errorMap);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.login(email, password);
      setAuthToken(response.access_token);
      setUser(response.user);
      addToast(t('auth.login.success'), 'success');
      navigate('/');
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, t('auth.login.error'));
      setErrors({ submit: errorMessage });
      addToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          {t('auth.email')}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
            errors.email ? 'border-error focus:ring-error' : 'border-neutral-300 focus:ring-primary-500'
          }`}
        />
        {errors.email && <p className="mt-1 text-sm text-error">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          {t('auth.password')}
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
            errors.password ? 'border-error focus:ring-error' : 'border-neutral-300 focus:ring-primary-500'
          }`}
        />
        {errors.password && <p className="mt-1 text-sm text-error">{errors.password}</p>}
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            {t('auth.login.forgotPassword')}
          </Link>
        </div>
      </div>

      {errors.submit && (
        <div className="p-3 bg-error/10 border border-error text-error rounded-lg text-sm">
          {errors.submit}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? t('auth.login.loading') : t('auth.login.submit')}
      </button>

      <p className="text-center text-sm text-neutral-600">
        {t('auth.login.noAccount')}{' '}
        <Link to="/register" className="text-primary-600 hover:text-primary-700 font-semibold">
          {t('auth.login.register')}
        </Link>
      </p>
    </form>
  );
}
